import { useCallback } from 'react';
import { useScheduleContext } from '@/pages/Schedule/contexts';
import { isGQLErrorOfType, type QueryError } from '@/utils/errors';
import {
  getSnippetsInUse,
  useSnippetIngredientInUseModal,
} from '../SnippetIngredientInUseModal';
import { CategoryDataContext, type CategoryData } from './CategoryDataContext';
import { useCreateCategory } from './hooks/useCreateCategory';
import { useRemoveCategory } from './hooks/useRemoveCategory';
import { useUpdateCategory } from './hooks/useUpdateCategory';
import { useUpdateCategorySortOrder } from './hooks/useUpdateCategorySortOrder';

type Props = {
  children: React.ReactNode;
};

export const ScheduleCategoryDataProvider = ({ children }: Props) => {
  const { schedule } = useScheduleContext();
  const { createCategory, isPending } = useCreateCategory();
  const { updateCategory } = useUpdateCategory();
  const { removeCategory } = useRemoveCategory();
  const { updateSortOrder } = useUpdateCategorySortOrder();
  const { open: openSnippetIngredientInUseModal } =
    useSnippetIngredientInUseModal();

  const handleRemoveError = useCallback(
    (error: QueryError) => {
      if (isGQLErrorOfType(error, 'CategoryInUse')) {
        openSnippetIngredientInUseModal('category', getSnippetsInUse(error));
        return true;
      }
      return false;
    },
    [openSnippetIngredientInUseModal]
  );

  const value: CategoryData = {
    categories: schedule?.categories ?? [],
    createCategory,
    isCreatePending: isPending,
    updateCategory,
    removeCategory,
    updateSortOrder,
    handleRemoveError,
  };

  return (
    <CategoryDataContext.Provider value={value}>
      {children}
    </CategoryDataContext.Provider>
  );
};
